import React from 'react';
import useLogPage from '../useLogPage';
import LogFilters from '../LogFilters';
import LogTable from '../LogTable';
import LogStatCards from '../LogStatCards';
import { TrendChart, GroupedBarChart } from '../LogCharts';
import { LogService } from '../../../services/activityLogService';

// Covers all actions an RBO (Retail Business Owner) can take in their panel
const ACTION_OPTIONS = [
  { value: 'PAGE_VIEW',       label: 'Page View'       },
  { value: 'CREATE',          label: 'Create'          },
  { value: 'UPDATE',          label: 'Update'          },
  { value: 'DELETE',          label: 'Delete'          },
  { value: 'EXPORT',          label: 'Export'          },
  { value: 'LOGIN',           label: 'Login'           },
  { value: 'LOGOUT',          label: 'Logout'          },
  { value: 'APPROVE',         label: 'Approve'         },
  { value: 'REJECT',          label: 'Reject'          },
  { value: 'DOWNLOAD',        label: 'Download'        },
];

const actionBadge = (v) => {
  const map = {
    page_view:  ['#DBEAFE', '#1E40AF'],
    create:     ['#D1FAE5', '#065F46'],
    update:     ['#FEF3C7', '#92400E'],
    delete:     ['#FEE2E2', '#991B1B'],
    export:     ['#E0F2FE', '#0369A1'],
    login:      ['#D1FAE5', '#065F46'],
    logout:     ['#F3F4F6', '#374151'],
    approve:    ['#D1FAE5', '#065F46'],
    reject:     ['#FEE2E2', '#991B1B'],
    download:   ['#E0F2FE', '#0369A1'],
  };
  const [bg, color] = map[v?.toLowerCase()] || ['#F3F4F6', '#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const statusBadge = (v) => {
  const map = {
    success: ['#D1FAE5', '#065F46'],
    failed:  ['#FEE2E2', '#991B1B'],
    pending: ['#FEF3C7', '#92400E'],
  };
  const [bg, color] = map[v?.toLowerCase()] || ['#F3F4F6', '#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const COLUMNS = [
  { key: 'id',            label: '#',          render: (v) => <span className="al-mono">{v}</span> },
  { key: 'business_name', label: 'Business',   render: (v) => v || '—' },
  { key: 'user_name',     label: 'User',       render: (v) => v || '—' },
  { key: 'email',         label: 'Email',      render: (v) => v || '—' },
  { key: 'module',        label: 'Module',     render: (v) => v || '—' },
  { key: 'action',        label: 'Action',     render: actionBadge },
  { key: 'status',        label: 'Status',     render: statusBadge },
  { key: 'reference_id',  label: 'Ref ID',     render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'remarks',       label: 'Details',    render: (v) => <span className="al-truncate" title={v}>{v || '—'}</span> },
  { key: 'ip_address',    label: 'IP',         render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'created_at',    label: 'Time',       render: (v) => v ? new Date(v).toLocaleString('en-IN') : '—' },
];

const RboActivityLog = () => {
  const lp = useLogPage(LogService.rboActivity);
  const s = lp.stats || {};

  const handleExport = () => {
    const { search, status, dateFrom, dateTo } = lp.filters;
    LogService.exportRboActivity({
      ...(search   ? { search }              : {}),
      ...(status   ? { status }              : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo   ? { date_to:   dateTo   } : {}),
      format: 'excel',
    });
  };

  const stats = [
    { label: 'Total Events',       value: lp.totalItems.toLocaleString(),              color: '#F97316' },
    { label: 'Unique Businesses',  value: (s.unique_businesses ?? 0).toLocaleString(), color: '#8B5CF6' },
    { label: 'Unique Users',       value: (s.unique_users      ?? 0).toLocaleString(), color: '#3B82F6' },
    { label: 'Actions Today',      value: (s.actions_today     ?? 0).toLocaleString(), color: '#10B981' },
  ];

  const barData = lp.donut.length
    ? lp.donut
    : Object.entries(s.breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="al-page">
      <LogStatCards stats={stats} />

      <div className="al-charts">
        <TrendChart title="RBO Activity Over Time" data={lp.trend} color="#F97316" />
        <GroupedBarChart
          title="Actions by Type"
          data={barData}
          bars={[{ key: 'value', label: 'Events' }]}
          xKey="name"
        />
      </div>

      <LogFilters
        filters={lp.filters}
        onFilterChange={lp.handleFilterChange}
        onApply={lp.handleApply}
        onReset={lp.handleReset}
        statusOptions={ACTION_OPTIONS}
        loading={lp.loading}
      />

      <LogTable
        title="RBO Activity Logs"
        columns={COLUMNS}
        rows={lp.rows}
        loading={lp.loading}
        error={lp.error}
        page={lp.page}
        totalPages={lp.totalPages}
        totalItems={lp.totalItems}
        pageSize={lp.filters.pageSize}
        onPageChange={lp.handlePageChange}
        onExport={handleExport}
        onRefresh={lp.handleRefresh}
      />
    </div>
  );
};

export default RboActivityLog;
